import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  alpha,
  useTheme,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Autocomplete,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CreditCard,
  Receipt,
  CheckCircle,
  XCircle,
  Crown,
} from 'lucide-react'
import Swal from 'sweetalert2'
import api from '../../api/axiosInstance'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import moment from 'moment-timezone'

const ModernProfilePage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('info')
  const [isEditing, setIsEditing] = useState(false)
  const [userInfo, setUserInfo] = useState({})
  const [editedInfo, setEditedInfo] = useState({})
  const [subscription, setSubscription] = useState(null)
  const [payments, setPayments] = useState([])

  // Tüm timezone listesi
  const timezones = moment.tz.names().map(tz => ({
    value: tz,
    label: `${tz} (UTC${moment.tz(tz).format('Z')})`,
    offset: moment.tz(tz).utcOffset(),
  }))

  // Claude Design palette tokens (navy-teal) — defined at the project root in theme.js
  const dc = theme.palette.designTokens

  const cardSx = {
    background: dc.card,
    border: `1px solid ${dc.cardBorder}`,
    borderRadius: '20px',
    backdropFilter: 'none',
  }

  const fieldLabelSx = {
    fontSize: 12,
    fontWeight: 600,
    color: dc.label,
    mb: 1,
    display: 'block',
  }

  // Compact input styling that matches the design's field boxes; stays readable
  // even while disabled (view mode).
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      background: dc.field,
      borderRadius: '10px',
      fontSize: 15,
      color: dc.value,
      '& fieldset': { borderColor: dc.fieldBorder },
      '&:hover fieldset': { borderColor: dc.fieldBorder },
      '&.Mui-focused fieldset': { borderColor: dc.accent, borderWidth: '1px' },
      '&.Mui-disabled': {
        background: dc.field,
        '& fieldset': { borderColor: dc.fieldBorder },
      },
    },
    // keep values fully legible when the field is disabled (view mode)
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: dc.value,
      color: dc.value,
    },
    '& .MuiInputBase-input': { paddingTop: '11px', paddingBottom: '11px' },
  }

  // Colorful emoji "logos" that replace the monochrome lucide glyphs
  const emoji = {
    user: '👤',
    mail: '✉️',
    lock: '🔒',
    phone: '📞',
    clock: '🕐',
    calendar: '📅',
    pin: '📍',
    flag: '🚩',
    building: '🏢',
    check: '✅',
    cross: '❌',
  }

  // Compact, emoji-adorned input. Rendered as a plain function call (not a
  // component element) so React keeps the same input instance across renders
  // and focus/caret are never lost while typing.
  const renderField = (labelText, icon, key, opts = {}) => (
    <Box sx={opts.full ? { gridColumn: '1 / -1' } : undefined}>
      <Typography component="span" sx={fieldLabelSx}>
        {labelText}
      </Typography>
      <TextField
        fullWidth
        hiddenLabel
        size="small"
        type={opts.type}
        placeholder={opts.placeholder}
        multiline={opts.multiline}
        rows={opts.rows}
        disabled={opts.alwaysDisabled || !isEditing}
        value={opts.type === 'password' ? undefined : (editedInfo[key] ?? opts.value ?? '')}
        onChange={
          opts.type === 'password' || !key
            ? opts.onChange
            : (e) => setEditedInfo({ ...editedInfo, [key]: e.target.value })
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={opts.multiline ? { alignSelf: 'flex-start', mt: 1 } : undefined}>
              <Box component="span" sx={{ fontSize: 17, lineHeight: 1 }}>{icon}</Box>
            </InputAdornment>
          ),
          endAdornment: opts.endAdornment,
        }}
        sx={inputSx}
      />
    </Box>
  )

  // A navy pill "tag" used in the header, matching the design chips
  const Tag = ({ children, onClick, gradient = false }) => (
    <Box
      component="span"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        px: gradient ? '14px' : '12px',
        py: '7px',
        borderRadius: '8px',
        fontSize: 13,
        fontWeight: gradient ? 700 : 600,
        cursor: onClick ? 'pointer' : 'default',
        ...(gradient
          ? { background: dc.gradientBtn, color: dc.onGradient }
          : { background: dc.chipBg, border: `1px solid ${dc.chipBorder}`, color: dc.chipText }),
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        ...(onClick && {
          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 14px rgba(16,185,129,0.35)' },
        }),
      }}
    >
      {children}
    </Box>
  )

  const getUserInfo = async () => {
    try {
      const response = await api.get('auth/me')
      console.log("response : ", response)
      const userData = response.data.user
      setUserInfo({
        id: userData.id,
        name: userData.name,
        surname: userData.surname,
        email: userData.email,
        phone: userData.phone,
        country: userData.country,
        city: userData.city,
        adress: userData.adress,
        created_at: String(userData.created_at).split('T')[0],
        updated_at: String(userData.updated_at).split('T')[0],
        isEmailVerified: userData.isEmailVerified,
        status: userData.status,
        timezone: userData.timezone,
        role: userData.role,
      })
      setEditedInfo({
        name: userData.name,
        surname: userData.surname || '',
        email: userData.email,
        password: '',
        phone: userData.phone || '',
        country: userData.country || '',
        city: userData.city || '',
        adress: userData.adress || '',
        timezone: userData.timezone,
      })
      setSubscription(userData.subscriptions.find(s => s.status === 'ACTIVE'))
      setPayments(userData.subscriptions.flatMap(s => s.payments))
    } catch (error) {
      console.error('Error fetching user info:', error)
    }
  }
  useEffect(() => {
    getUserInfo()
  }, [])

  const handleSaveChanges = async () => {
    try {
      if (editedInfo.password === '') {
        delete editedInfo.password;
      }

      const response = await api.put('auth/register-change', editedInfo)
      setUserInfo({ ...userInfo, ...editedInfo })
      setIsEditing(false)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Bilgileriniz güncellendi',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.response?.data?.message || 'Bir hata oluştu',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    }
  }

  const handleVerifyEmail = async () => {
    try {
      const response = await api.post('auth/send-verification-email')
      Swal.fire({
        icon: 'warring',
        title: 'Warring',
        text: 'The email verification link has been sent to your email address !',
        confirmButtonText: 'Okey',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'An Error',
        confirmButtonText: 'Error',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    }
  }

  const handleCancelEdit = () => {
    setEditedInfo({
      name: userInfo.name,
      surname: userInfo.surname,
      email: userInfo.email,
      phone: userInfo.phone,
      country: userInfo.country,
      city: userInfo.city,
      adress: userInfo.adress,
      timezone: userInfo.timezone,
    })
    setIsEditing(false)
  }

  const tabs = [
    { id: 'info', label: 'Info', emoji: '👤' },
    { id: 'subscription', label: 'Subscription', emoji: '👑' },
    { id: 'payments', label: 'Payments', emoji: '💳' },
  ]

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ width: '100%' }}
    >
      {/* Header Card with Avatar */}
      <Box sx={{ ...cardSx, overflow: 'hidden', mb: 3 }}>
        {/* Banner */}
        <Box
          sx={{
            height: 148,
            background: dc.gradient,
            position: 'relative',
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              position: 'absolute',
              left: 40,
              bottom: -56,
              width: 112,
              height: 112,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #34d399, #059669)',
              border: `4px solid ${dc.card}`,
              boxShadow: '0 0 24px rgba(52,211,153,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 44,
              fontWeight: 800,
              color: '#ffffff',
            }}
          >
            {userInfo.name?.charAt(0).toUpperCase()}
          </Box>
          {/* Name */}
          <Typography
            sx={{
              position: 'absolute',
              left: 172,
              top: 40,
              fontSize: 30,
              fontWeight: 800,
              color: '#ffffff',
              letterSpacing: '-0.02em',
            }}
          >
            {[userInfo.name, userInfo.surname].filter(Boolean).join(' ')}
          </Typography>
        </Box>

        {/* Tags row */}
        <Box
          sx={{
            pt: '34px',
            pr: '40px',
            pb: '24px',
            pl: '172px',
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Tag>{emoji.building} RahatUp</Tag>
          {userInfo.city?.trim() && <Tag>{emoji.pin} {userInfo.city.trim()}</Tag>}
          {userInfo.country?.trim() && <Tag>{emoji.flag} {userInfo.country.trim()}</Tag>}
          <Tag>{userInfo.status ? `${emoji.check} Active` : `${emoji.cross} Passive`}</Tag>
          {!userInfo.isEmailVerified && (
            <Tag gradient onClick={() => handleVerifyEmail()}>
              {emoji.mail} Verify Email
            </Tag>
          )}
        </Box>
      </Box>

      {/* Tabs - segmented pill control with sliding indicator */}
      <Box sx={{ mb: 3, maxWidth: '100%', overflowX: 'auto' }}>
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            p: '6px',
            gap: '4px',
            borderRadius: '16px',
            background: dc.card,
            border: `1px solid ${dc.cardBorder}`,
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id
            return (
              <Box
                key={tab.id}
                component="button"
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.75,
                  py: 1.5,
                  border: 'none',
                  background: 'transparent',
                  borderRadius: '11px',
                  fontFamily: 'inherit',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  color: isActive ? dc.onGradient : '#94a3b8',
                  transition: 'color 0.25s ease',
                  '&:hover': {
                    color: isActive ? dc.onGradient : dc.accent,
                  },
                }}
              >
                {isActive && (
                  <Box
                    component={motion.div}
                    layoutId="profileTabIndicator"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: -1,
                      borderRadius: '11px',
                      background: dc.gradientBtn,
                      boxShadow: '0 4px 16px rgba(16,185,129,0.35)',
                    }}
                  />
                )}
                <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                  {tab.emoji}
                </Box>
                <span>{tab.label}</span>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'info' && (
          <motion.div
            key="info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ ...cardSx, p: '32px 40px 40px' }}>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', mb: '28px' }}>
                Kullanıcı Bilgileri
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: '20px 32px',
                }}
              >
                {renderField('Ad', emoji.user, 'name')}
                {renderField('Soyad', emoji.user, 'surname')}
                {renderField('E-posta', emoji.mail, 'email', {
                  endAdornment: (
                    <InputAdornment position="end">
                      {userInfo.isEmailVerified ? (
                        <CheckCircle size={16} color={theme.palette.success.main} />
                      ) : (
                        <XCircle size={16} color={theme.palette.warning.main} />
                      )}
                    </InputAdornment>
                  ),
                })}
                {isEditing &&
                  renderField('Yeni Şifre', emoji.lock, 'password', {
                    type: 'password',
                    placeholder: 'Değiştirmek için girin',
                    onChange: (e) => setEditedInfo({ ...editedInfo, password: e.target.value }),
                  })}
                {renderField('Telefon', emoji.phone, 'phone')}

                {/* Time Zone - Autocomplete */}
                <Box>
                  <Typography component="span" sx={fieldLabelSx}>
                    Time Zone
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disabled={!isEditing}
                    value={timezones.find((tz) => tz.value === editedInfo.timezone) || null}
                    onChange={(event, newValue) =>
                      setEditedInfo({ ...editedInfo, timezone: newValue ? newValue.value : '' })
                    }
                    options={timezones}
                    getOptionLabel={(option) => option.label}
                    groupBy={(option) => option.value.split('/')[0]}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        hiddenLabel
                        placeholder="Time Zone"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <Box component="span" sx={{ fontSize: 17, lineHeight: 1 }}>
                                  {emoji.clock}
                                </Box>
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={inputSx}
                      />
                    )}
                    sx={{ '& .MuiAutocomplete-popupIndicator': { color: dc.accent } }}
                  />
                </Box>

                {renderField('Country', emoji.flag, 'country')}
                {renderField('City', emoji.pin, 'city')}
                {renderField('Kayıt Tarihi', emoji.calendar, null, {
                  alwaysDisabled: true,
                  value: userInfo.created_at,
                })}
                {renderField('Adres', emoji.pin, 'adress', { full: true, multiline: true, rows: 2 })}
              </Box>

              {/* Bottom action bar: edit by default, save/cancel while editing */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: '32px' }}>
                {!isEditing ? (
                  <Box
                    component="button"
                    onClick={() => setIsEditing(true)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: '22px',
                      py: '11px',
                      borderRadius: '10px',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: 700,
                      background: dc.gradientBtn,
                      color: dc.onGradient,
                      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(16,185,129,0.35)' },
                    }}
                  >
                    ✏️ Düzenle
                  </Box>
                ) : (
                  <>
                    <Box
                      component="button"
                      onClick={handleCancelEdit}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: '22px',
                        py: '11px',
                        borderRadius: '10px',
                        border: `1px solid ${alpha(theme.palette.error.main, 0.5)}`,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: 700,
                        background: 'transparent',
                        color: theme.palette.error.main,
                        transition: 'background 0.2s ease',
                        '&:hover': { background: alpha(theme.palette.error.main, 0.1) },
                      }}
                    >
                      ✕ İptal
                    </Box>
                    <Box
                      component="button"
                      onClick={handleSaveChanges}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: '22px',
                        py: '11px',
                        borderRadius: '10px',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: 700,
                        background: dc.gradientBtn,
                        color: dc.onGradient,
                        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(16,185,129,0.35)' },
                      }}
                    >
                      💾 Kaydet
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </motion.div>
        )}

        {activeTab === 'subscription' && (
          <motion.div
            key="subscription"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card
              sx={{
                background: dc.card,
                backdropFilter: 'none',
                border: `1px solid ${dc.cardBorder}`,
                borderRadius: '20px',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Abonelik Bilgileri
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/user/subscription")}
                    sx={{
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    Planı Değiştir
                  </Button>
                </Box>

                {subscription ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          background: `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.1
                          )}, ${alpha(theme.palette.primary.main, 0.05)})`,
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                              borderRadius: 2,
                            }}
                          >
                            <Crown size={32} color={theme.palette.primary.main} />
                          </Box>
                          <Box>
                            <Typography variant="h4" fontWeight={900}>
                              {subscription.plan.name}
                            </Typography>
                            <Chip
                              label={subscription.status === 'ACTIVE' ? 'Aktif' : 'Pasif'}
                              color={subscription.status === 'ACTIVE' ? 'success' : 'default'}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Stack>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color={alpha('#fff', 0.6)}>
                              Başlangıç
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {dayjs(subscription.currentPeriodStart).format('DD.MM.YYYY')}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color={alpha('#fff', 0.6)}>
                              Bitiş
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {dayjs(subscription.currentPeriodEnd).format('DD.MM.YYYY')}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.1) }} />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="h5" fontWeight={700}>
                              ₺{subscription.plan.price / 100}
                              <Typography
                                component="span"
                                sx={{ fontSize: '1rem', color: alpha('#fff', 0.6), ml: 1 }}
                              >
                                / {subscription.plan.durationDays + ' gün'}
                              </Typography>
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                          Plan Özellikleri
                        </Typography>
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                          {subscription.plan.features && Object.entries(subscription.plan.features).map(([key, value], idx) => (
                            <Stack key={idx} direction="row" spacing={1} alignItems="center">
                              {value ? (
                                <CheckCircle size={16} color={theme.palette.success.main} />
                              ) : (
                                <XCircle size={16} color={theme.palette.error.main} />
                              )}
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {key.replace(/_/g, ' ')}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>

                        <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.1) }} />

                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          Limitler
                        </Typography>
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                          {subscription.plan.limits && Object.entries(subscription.plan.limits).map(([key, value], idx) => (
                            <Box key={idx}>
                              <Typography variant="caption" color={alpha('#fff', 0.6)}>
                                {key.replace(/_/g, ' ')}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {value}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        mb: 3,
                      }}
                    >
                      <Crown size={40} color={theme.palette.primary.main} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Aktif Abonelik Bulunamadı
                    </Typography>
                    <Typography color={alpha('#fff', 0.6)} sx={{ mb: 3 }}>
                      Hizmetlerimizden yararlanmak için bir plan seçin
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate("/user/subscription")}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        color: '#000',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        },
                      }}
                    >
                      Abonelik Satın Al
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {activeTab === 'payments' && (
          <motion.div
            key="payments"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card
              sx={{
                background: dc.card,
                backdropFilter: 'none',
                border: `1px solid ${dc.cardBorder}`,
                borderRadius: '20px',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Ödeme Geçmişi
                </Typography>

                {payments.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    sx={{
                      mt: 3,
                      background: 'transparent',
                      boxShadow: 'none',
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, borderColor: alpha('#fff', 0.1) }}>
                            Tarih
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, borderColor: alpha('#fff', 0.1) }}>
                            Fatura No
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, borderColor: alpha('#fff', 0.1) }}>
                            Tutar
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, borderColor: alpha('#fff', 0.1) }}>
                            Yöntem
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, borderColor: alpha('#fff', 0.1) }}>
                            Durum
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, borderColor: alpha('#fff', 0.1) }}>
                            İşlem
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow
                            key={payment.id}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                              },
                            }}
                          >
                            <TableCell sx={{ borderColor: alpha('#fff', 0.1) }}>
                              {dayjs(payment.createdAt).format('DD.MM.YYYY HH:mm')}
                            </TableCell>
                            <TableCell sx={{ borderColor: alpha('#fff', 0.1), fontFamily: 'monospace' }}>
                              #{payment.id}
                            </TableCell>
                            <TableCell sx={{ borderColor: alpha('#fff', 0.1), fontWeight: 600 }}>
                              {payment.amount} {payment.currency}
                            </TableCell>
                            <TableCell sx={{ borderColor: alpha('#fff', 0.1) }}>
                              {payment.provider}
                            </TableCell>
                            <TableCell sx={{ borderColor: alpha('#fff', 0.1) }}>
                              <Chip
                                label={
                                  payment.status === 'SUCCESS'
                                    ? 'Ödendi'
                                    : payment.status === 'PENDING'
                                      ? 'Beklemede'
                                      : 'Başarısız'
                                }
                                color={
                                  payment.status === 'SUCCESS'
                                    ? 'success'
                                    : payment.status === 'PENDING'
                                      ? 'warning'
                                      : 'error'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ borderColor: alpha('#fff', 0.1) }}>
                              <Button
                                size="small"
                                startIcon={<Receipt size={16} />}
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  },
                                }}
                              >
                                Fatura
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        mb: 3,
                      }}
                    >
                      <CreditCard size={40} color={theme.palette.primary.main} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Ödeme Geçmişi Bulunamadı
                    </Typography>
                    <Typography color={alpha('#fff', 0.6)}>
                      Henüz herhangi bir ödeme işlemi gerçekleştirilmemiş
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default ModernProfilePage